import {type DatesRangeValue} from '@mantine/dates'
import {format, isValid, parseISO} from 'date-fns'

import type {JobLifecycleStatus} from './types'

export type JobListSearch = {
    name?: string
    customer?: string
    service?: string
    from?: string
    to?: string
    status?: string
    sort?: string
}

export type JobSortField =
    | 'createdAt'
    | 'startDate'
    | 'name'
    | 'customerName'
    | 'serviceTypeName'
    | 'status'

export type JobSortState = {
    field: JobSortField
    direction: 'asc' | 'desc'
}

const DEFAULT_SORT_STATE: JobSortState = {
    field: 'createdAt',
    direction: 'desc',
}

export const DEFAULT_JOB_STATUS_FILTER: JobLifecycleStatus[] = ['estimate', 'in_progress']

const JOB_LIFECYCLE_STATUSES = new Set<JobLifecycleStatus>([
    'estimate',
    'in_progress',
    'closed',
    'canceled',
])

const VALID_SORT_VALUES = new Set<string>([
    'createdAt-desc',
    'createdAt-asc',
    'startDate-asc',
    'startDate-desc',
    'name-asc',
    'name-desc',
    'customerName-asc',
    'customerName-desc',
    'serviceTypeName-asc',
    'serviceTypeName-desc',
    'status-asc',
    'status-desc',
])

function parseTrimmedString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined
    }

    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
}

function parseDateSearchParam(value: unknown): string | undefined {
    const trimmed = parseTrimmedString(value)
    if (!trimmed) {
        return undefined
    }

    const parsed = parseISO(trimmed)
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : undefined
}

export function parseStatusSearchParam(value: unknown): JobLifecycleStatus[] {
    const values = Array.isArray(value) ? value : [value]

    return [
        ...new Set(
            values.flatMap((entry) => {
                const trimmed = parseTrimmedString(entry)
                return trimmed ? trimmed.split(',').map((status) => status.trim()) : []
            }),
        ),
    ].filter((entry): entry is JobLifecycleStatus =>
        JOB_LIFECYCLE_STATUSES.has(entry as JobLifecycleStatus),
    )
}

export function serializeStatusSearchParam(statuses: JobLifecycleStatus[]): string | undefined {
    const normalizedStatuses = [...new Set(statuses)].filter((status) =>
        JOB_LIFECYCLE_STATUSES.has(status),
    )
    return normalizedStatuses.length > 0 ? normalizedStatuses.join(',') : undefined
}

export function validateJobListSearch(search: Record<string, unknown>): JobListSearch {
    const result: JobListSearch = {}

    const name = parseTrimmedString(search['name'])
    if (name) {
        result.name = name
    }

    const customer = parseTrimmedString(search['customer'])
    if (customer) {
        result.customer = customer
    }

    const service = parseTrimmedString(search['service'])
    if (service) {
        result.service = service
    }

    const from = parseDateSearchParam(search['from'])
    if (from) {
        result.from = from
    }

    const to = parseDateSearchParam(search['to'])
    if (to) {
        result.to = to
    }

    const status = serializeStatusSearchParam(parseStatusSearchParam(search['status']))
    if (status) {
        result.status = status
    }

    const sort = parseTrimmedString(search['sort'])
    if (sort && VALID_SORT_VALUES.has(sort)) {
        result.sort = sort
    }

    return result
}

export function sortStateToSearchParam(sortState: JobSortState): string | undefined {
    const value = `${sortState.field}-${sortState.direction}`
    if (value === `${DEFAULT_SORT_STATE.field}-${DEFAULT_SORT_STATE.direction}`) {
        return undefined
    }

    return value
}

export function searchToSortState(search: JobListSearch): JobSortState {
    if (!search.sort || !VALID_SORT_VALUES.has(search.sort)) {
        return DEFAULT_SORT_STATE
    }

    const separatorIndex = search.sort.lastIndexOf('-')
    const field = search.sort.slice(0, separatorIndex) as JobSortField
    const direction = search.sort.slice(separatorIndex + 1) as JobSortState['direction']
    return {field, direction}
}

export function searchToDateRange(search: JobListSearch): DatesRangeValue {
    const from = search.from ? parseISO(search.from) : null
    const to = search.to ? parseISO(search.to) : null

    return [from != null && isValid(from) ? from : null, to != null && isValid(to) ? to : null]
}

export function buildJobListSearch(params: {
    name?: string
    customerId?: string | null
    serviceTypeName?: string
    scheduleDateRange: DatesRangeValue
    statusFilter: JobLifecycleStatus[]
    sortState: JobSortState
}): JobListSearch {
    const result: JobListSearch = {}

    const name = params.name?.trim()
    if (name) {
        result.name = name
    }

    if (params.customerId) {
        result.customer = params.customerId
    }

    const service = params.serviceTypeName?.trim()
    if (service) {
        result.service = service
    }

    const [fromDate, toDate] = params.scheduleDateRange
    if (fromDate) {
        result.from = format(fromDate, 'yyyy-MM-dd')
    }
    if (toDate) {
        result.to = format(toDate, 'yyyy-MM-dd')
    }

    const status = serializeStatusSearchParam(params.statusFilter)
    if (status) {
        result.status = status
    }

    const sort = sortStateToSearchParam(params.sortState)
    if (sort) {
        result.sort = sort
    }

    return result
}

export function jobListSearchEquals(left: JobListSearch, right: JobListSearch): boolean {
    return (
        left.name === right.name &&
        left.customer === right.customer &&
        left.service === right.service &&
        left.from === right.from &&
        left.to === right.to &&
        left.status === right.status &&
        left.sort === right.sort
    )
}
